import gsap from 'gsap';
import { Box3, Object3D, Sprite, Vector3 } from 'three';
import { ref, watchEffect } from 'vue';

import { lerp, map } from '@resn/gozer-math';
import { usePane } from '@resn/gozer-vue';

import { useFlare } from './useFlare';

export const useShineWipe = (name = 'ShineWipe') => {
    const dimensions = new Vector3();
    const box = new Box3();

    const tweens = { flarePr: 0 };

    const uShineWipePr = ref(0);
    const uShineWipeAngle = ref(0);
    const uShineWipeThickness = ref(0.4);
    const uShineWipeIntensity = ref(0.5);
    const uShineWipeLightTint = ref('#fff2fa');

    const flare = useFlare({ name });

    const flare1 = flare.mesh;
    const flare2 = new Sprite(flare.material);
    const flare3 = new Sprite(flare.material);
    flare2.scale.setScalar(flare.refs.uFlareScale.value);
    flare3.scale.setScalar(flare.refs.uFlareScale.value);
    flare1.renderOrder = 2;
    flare2.renderOrder = 2;
    flare3.renderOrder = 2;

    watchEffect(() => {
        flare2.scale.setScalar(flare.refs.uFlareScale.value);
        flare3.scale.setScalar(flare.refs.uFlareScale.value);
    });

    const object = new Object3D();
    object.add(flare1);
    object.add(flare2);
    object.add(flare3);
    let tl;
    let activeFlares = new Set([1, 2]);

    const setActiveFlares = (flares = [1, 2]) => {
        activeFlares = new Set(flares);
        flare1.visible = activeFlares.has(1);
        flare2.visible = activeFlares.has(2);
        flare3.visible = activeFlares.has(3);
    };

    setActiveFlares();

    const updatePositions = () => {
        const { flarePr } = tweens;

        const halfx = dimensions.x * 0.5;
        const halfy = dimensions.y * 0.5;
        const x = lerp(flarePr, halfx, -halfx);
        const y = lerp(flarePr, -halfy, halfy);

        if (activeFlares.has(1)) flare1.position.set(x, halfy, 0);
        if (activeFlares.has(2)) flare2.position.set(-halfx, y, 0);
        if (activeFlares.has(3)) flare3.position.set(halfx, y, 0);
    };

    const show = ({ delay = 0, intensity = 1, flares = [1, 2] } = {}) => {
        setActiveFlares(flares);

        const showFlares = intensity > 0.2 && activeFlares.size > 0;
        const start = lerp(intensity, 0.3, 0);
        const end = lerp(intensity, 0.3, 1);
        const intensityMap = lerp(intensity, 0.5, 1.5);
        const thickness = lerp(intensity, 0.4, 0.5);

        uShineWipeIntensity.value = intensityMap;
        uShineWipeThickness.value = thickness;

        const alphaTo = map(intensityMap, 0.5, 1.5, 0.3, 1, true);
        const flareDelay = 0.3;

        tl?.kill();
        tl = gsap.timeline({ delay, onUpdate: updatePositions });
        tl.fromTo(uShineWipePr, { value: 0 }, { value: 1, duration: 2, ease: 'sine.out' }, 0);

        if (showFlares) {
            tl.fromTo(
                tweens,
                { flarePr: start },
                { flarePr: end, duration: 0.8, ease: 'power2.out' },
                flareDelay
            );

            tl.fromTo(
                flare.refs.uFlareAlpha,
                { value: 0 },
                { value: alphaTo, duration: 0.4, ease: 'sine.out' },
                flareDelay
            );
            tl.fromTo(
                flare.refs.uFlareRotation,
                { value: 0 },
                { value: 6.28, duration: 2, ease: 'linear' },
                flareDelay
            );
            tl.to(
                flare.refs.uFlareAlpha,
                { value: 0, duration: 0.4, ease: 'sine.out' },
                flareDelay + 0.6
            );
        }
    };

    const getBoxDimensions = (object) => {
        box.setFromObject(object).getSize(dimensions);

        updatePositions();
    };

    const paneOptions = [
        {
            name: 'uShineWipePr',
            value: uShineWipePr,
            options: { min: 0, max: 1, step: 0.01 },
        },
        {
            name: 'uShineWipeThickness',
            value: uShineWipeThickness,
            options: { min: 0, max: 3, step: 0.01 },
        },
        {
            name: 'uShineWipeIntensity',
            value: uShineWipeIntensity,
            options: { min: 0, max: 3, step: 0.01 },
        },
    ];

    // usePane(paneOptions, { title: name });

    const refs = {
        uShineWipePr,
        uShineWipeAngle,
        uShineWipeThickness,
        uShineWipeIntensity,
        uShineWipeLightTint,
        ...flare.refs,
    };

    return { object, flare1, flare2, flare3, refs, show, getBoxDimensions };
};

/**
 * Three.js uniforms object (`{ name: { value } }`) for radial pulse gradient.
 */
export const uniforms = () => {
    return {
        uShineWipePr: { value: 0 },
        uShineWipeAngle: { value: 0 },
        uShineWipeThickness: { value: 0 },
        uShineWipeIntensity: { value: 0 },
        uShineWipeLightTint: { value: new Vector3(1.0, 0.95, 0.98) },
        uFlareRayWidth: { value: 0.002 },
        uFlareRayFade: { value: 0.4 },
        uFlareRayIntensity: { value: 1.5 },
        uFlareAlpha: { value: 1 },
    };
};

/**
 * GLSL snippets: inject `uniforms` before your fragment `main`, `funcs` with the library + include guard.
 */
export const shaderChunks = {
    uniforms: /* glsl */ `
        uniform float uShineWipePr;
        uniform float uShineWipeAngle;
        uniform float uShineWipeThickness;
        uniform float uShineWipeIntensity;
        uniform vec3 uShineWipeLightTint;
        uniform float uFlareRayWidth;
        uniform float uFlareRayFade;
        uniform float uFlareRayIntensity;
        uniform float uFlareAlpha;
    `,
    funcs: /* glsl */ `
        #ifndef FNC_SHINE_WIPE
        #define FNC_SHINE_WIPE
        
        vec3 shineWipe(vec3 inputColor, vec2 uv, float revealPr, float angle, float thickness) {
            // 1. Control the "Width" of the blown-out wave
            // Smaller = sharper line, Larger = softer glow
            float waveWidth = max(thickness, 1e-4);

            // rotate() is around (0.5): shineUv.x spans [0.5 - √2/2, 0.5 + √2/2] at worst (45°).
            // Pad by several σ so a thick band is fully off-quad at revealPr 0 / 1.
            const float SHINE_UV_X_MIN = 0.5 - 0.7071067811865476;
            const float SHINE_UV_X_MAX = 0.5 + 0.7071067811865476;
            const float EDGE_SIGMAS = 4.0;
            float edgePad = EDGE_SIGMAS * waveWidth;
            float lineCenter = mix(SHINE_UV_X_MIN - edgePad, SHINE_UV_X_MAX + edgePad, revealPr);

            vec2 shineUv = rotate(uv, toRadians(angle));
            float dist = abs(shineUv.x - lineCenter);
            float wave = exp(-(dist * dist) / (waveWidth * waveWidth));

            // 4. Intensity & Tint
            // We multiply by a factor (e.g., 1.5) to ensure it truly "blows out" to white
            float blowoutIntensity = wave * uShineWipeIntensity;
            vec3 lightTint = vec3(1.0, 0.95, 0.98); // Slightly warm white
            
            // 5. Apply the effect
            // We add the light to the base. Values > 1.0 naturally clamp to white.
            vec3 finalColor = inputColor.rgb + (lightTint * blowoutIntensity);

            return finalColor;
        }

        #endif
    `,
};
